import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {DiscountCodeFormValuesV2, SingleDiscountCodeApiResponseV2} from "../types";
import {buildV2CreatePayload} from "../create-discount-code-service-v2";
import {useGetDiscountCodesContext} from "../../get-discounts/context";
import {AnalyticsEvent} from "../../../../utils/analytics";
import {useClient} from "../../../../utils/hooks";
import {useGetOfferings} from "../../../../react-query-hooks/offerings";

interface HookResult {
  createDiscountCode(values: DiscountCodeFormValuesV2): void;
  wasDiscountCodeCreated: boolean;
  isCreatingDiscountCode: boolean;
  responseData?: SingleDiscountCodeApiResponseV2;
  handleModalOk: () => void;
  isSuccessModalOpen: boolean;
}

export function useCreateDiscountCodeV2(): HookResult {
  const navigate = useNavigate();
  const [openSuccessfulModal, setOpenSuccessfulModal] = useState<boolean>(false);
  const {
    state: {discountCodes, per_page, currentPage},
  } = useGetDiscountCodesContext();

  const offeringsQuery = useGetOfferings();
  const allOfferings = offeringsQuery.data ?? [];

  const currentDiscountCodesLength = discountCodes.length;
  const isEqualToPageLimit = currentDiscountCodesLength === per_page;
  const queryClient = useQueryClient();
  const client = useClient("DISCOUNTS");

  const mutation = useMutation<
    {data: SingleDiscountCodeApiResponseV2},
    AxiosError<{message: string}>,
    DiscountCodeFormValuesV2,
    unknown
  >({
    mutationFn: (values) => {
      const payload = buildV2CreatePayload(values, allOfferings);
      return client(`discounts`, {
        method: "POST",
        data: payload,
      });
    },
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Discounts", "Discounts create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Discounts", "Discounts create success");
      setOpenSuccessfulModal(true);
      if (isEqualToPageLimit) {
        queryClient.invalidateQueries({queryKey: ["discounts", currentPage + 1]});
      }
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["discounts"]}),
  });

  const createDiscountCode = (values: DiscountCodeFormValuesV2) => {
    mutation.mutate(values);
  };

  const handleModalOk = () => {
    setOpenSuccessfulModal(false);
    navigate("/discounts");
  };

  return {
    createDiscountCode,
    wasDiscountCodeCreated: mutation.isSuccess,
    isCreatingDiscountCode: mutation.isLoading,
    responseData: mutation.data?.data,
    handleModalOk,
    isSuccessModalOpen: openSuccessfulModal,
  };
}
